import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import background from '/Background.png';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useAcceptInvitation, useInvitationPreview } from '@/hooks/useInvitations';
import {
  InvitationAcceptForm,
  InvitationAcceptSchema,
  UserRole,
} from '@/schema/invitation';

/**
 * Public landing page for an invite link. The e-mail, clinic and role come from the
 * invitation and cannot be changed here — that is the whole point of replacing open
 * self-registration.
 */
export function AcceptInvite() {
  useDocumentTitle('Accept invitation');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const { data: preview, isLoading, isError, error } = useInvitationPreview(token);
  const accept = useAcceptInvitation();

  const form = useForm<InvitationAcceptForm>({
    resolver: zodResolver(InvitationAcceptSchema),
    defaultValues: { username: '', password: '', confirmPassword: '', firstName: '', lastName: '', phone: '' },
  });

  useEffect(() => {
    if (!preview) return;
    form.reset({
      username: '',
      password: '',
      confirmPassword: '',
      firstName: preview.firstName ?? '',
      lastName: preview.lastName ?? '',
      phone: '',
    });
  }, [preview, form]);

  const handleSubmit = ({ confirmPassword: _confirmPassword, ...data }: InvitationAcceptForm) => {
    if (!token) return;
    accept.mutate(
      { token, data },
      {
        onSuccess: () => {
          toast.success('Account created — you can sign in now.');
          navigate('/login', { replace: true });
        },
        onError: () => toast.error('Could not create the account. The link may have just expired.'),
      },
    );
  };

  const shell = (children: React.ReactNode) => (
    <div className="flex h-full w-full items-center justify-center text-white">
      <img src={background} alt="" className="absolute h-screen w-screen object-cover blur-lg" />
      <div className="z-10 w-full max-w-md rounded-3xl bg-content-dark p-8 shadow-lg shadow-black/30">
        {children}
      </div>
    </div>
  );

  if (isLoading) {
    return shell(
      <div className="flex justify-center py-10">
        <LoadingSpinner size={60} className="text-primary" />
      </div>,
    );
  }

  if (isError || !preview) {
    // 410 means the link was used, revoked or expired; anything else means it never existed.
    const isGone =
      typeof error === 'object' && error !== null && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status === 410
        : false;

    return shell(
      <div className="flex flex-col gap-4 text-center">
        <h1 className="text-2xl font-bold">This link no longer works</h1>
        <p className="text-sm text-white/60">
          {isGone
            ? 'It has already been used, was revoked, or has expired. Ask your administrator for a new one.'
            : 'We could not find this invitation. Check that the whole link was copied.'}
        </p>
        <Button type="button" variant="ghost-light" onClick={() => navigate('/login')}>
          Go to sign in
        </Button>
      </div>,
    );
  }

  const isClinicUser = preview.role === UserRole.user;

  return shell(
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-4">
        <header className="mb-2 space-y-1 text-center">
          <h1 className="text-2xl font-bold">Welcome to Zubee Factory</h1>
          <p className="text-sm text-white/60">
            {preview.clinicName
              ? `You have been invited to ${preview.clinicName} as ${preview.email}.`
              : `You have been invited as ${preview.email}.`}
          </p>
        </header>

        <FormField
          control={form.control}
          name="username"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Username *</FormLabel>
              <FormControl>
                <Input type="text" autoComplete="username" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {isClinicUser ? (
          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First name *</FormLabel>
                  <FormControl>
                    <Input type="text" {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last name *</FormLabel>
                  <FormControl>
                    <Input type="text" {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        ) : null}

        {isClinicUser ? (
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone</FormLabel>
                <FormControl>
                  <Input type="tel" {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password *</FormLabel>
              <FormControl>
                <Input type="password" autoComplete="new-password" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Confirm password *</FormLabel>
              <FormControl>
                <Input type="password" autoComplete="new-password" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" size="lg" className="mt-2 font-bold" disabled={accept.isPending}>
          {accept.isPending ? 'CREATING…' : 'CREATE ACCOUNT'}
        </Button>
      </form>
    </Form>,
  );
}

export default AcceptInvite;
